(ns manage.content-configuration-page.placeholder-expression.view
  (:require ["@mui/material/Box$default" :as Box]
            ["@mui/material/Button$default" :as Button]
            ["@mui/material/Icon$default" :as Icon]
            ["@mui/material/Stack$default" :as Stack]
            [common.ui.entity :refer [query-param->config]]
            [common.ui.list :as ui.list]
            [common.ui.store :as store]
            [common.util :as util]
            [common.widgets.layout :refer [stack-divider]]
            [manage.content-configuration-page.placeholder-expression.db
             :refer [pull-pattern-collection-item
                     pull-pattern-placeholder-expression]]
            [manage.content-configuration-page.placeholder-expression.product-helper]
            [manage.content-configuration-page.placeholder-expression.singular
             :as singular]
            [manage.content-configuration-page.placeholder.db
             :refer [pull-pattern-placeholder]]
            [manage.content-configuration-page.view.list :as lv]
            [manage.content-configuration-page.view.slide :as slide]
            [re-frame.core :as rf]
            [uix.core :as uix :refer [$]]))

(defmulti placeholder-expression
  (fn [_ placeholder _] (:placeholder/type placeholder))
  :default :singular)

(defmethod placeholder-expression :singular
  [options placeholder phe]
  [singular/text options placeholder phe])

(defmethod placeholder-expression :text
  [options placeholder phe]
  [singular/text (assoc options :multi-line? true) placeholder phe])

(defmethod placeholder-expression :click
  [options placeholder phe]
  [singular/text (assoc options :multi-line? false) placeholder phe])

(defmethod placeholder-expression :audio
  [options placeholder phe]
  [singular/audio (assoc options :multi-line? false) placeholder phe])

(defmethod placeholder-expression :image
  [options placeholder phe]
  [singular/image (assoc options :multi-line? false) placeholder phe])

(defmethod placeholder-expression :video
  [options placeholder phe]
  [singular/video (assoc options :multi-line? false) placeholder phe])

(defn phe->placeholder
  [phe pull-pattern]
  @(rf/subscribe [:placeholder-expression/placeholder phe pull-pattern]))

(defn visualization-order [phe]
  @(rf/subscribe [:placeholder-expression/visualization-order phe]))

(defn placeholder-expression-read-only-if-published
  [phe]
  (let [published? @(rf/subscribe [:resource.content-function-version/active-version? "published"])
        placeholder (phe->placeholder (:eid phe) pull-pattern-placeholder)]
    [placeholder-expression {:disabled? published?
                             :data-testid "placeholder-expression-input"} placeholder phe]))

(defn label-placeholder-name [phe]
  (let [placeholder (phe->placeholder (:eid phe) [:placeholder/name])]
    (:placeholder/name placeholder)))

(defn entity-view-cfg [phe]
  ;; placeholder type is very static (cannot be changed by the user) so it
  ;; should not be a problem to have a subscription on it in this entity cfg
  (let [placeholder-type (:placeholder/type (phe->placeholder (:eid phe) [:placeholder/type]))]
    (cond-> {:entity/ref phe
             :entity/label [label-placeholder-name phe]
             :entity/icon [:placeholder/icon placeholder-type]
             :validation/pull-pattern pull-pattern-placeholder-expression
             :list-item/details [placeholder-expression-read-only-if-published phe]}
      (not= placeholder-type :collection)
      (assoc :validation/schema :singular-placeholder-expression))))

;; code starting here is specific to the collection placeholder expression

(defn list-collection-item-cfg [cphe]
  {:entity/ref cphe
   :list/items :collection-placeholder-expression/item
   :list/sort-by :collection-item/order
   :list/reorderable? true
   :list/add-item-cfg
   (cond->
     [{:add-item/add-button-label "Add collection item"
       :add-item/add-fn #(rf/dispatch [:collection-placeholder-expression/create-collection-item (:eid cphe)])
       :add-item/ignore-input? true}]
     @(rf/subscribe [:placeholder-expression/is-override? cphe])
     (conj
      {:add-item/add-button-label "Copy from default"
       :add-item/add-button-props {:disabled @(rf/subscribe [:collection-placeholder-expression/has-collection-items? cphe])}
       :add-item/add-fn #(rf/dispatch [:collection-placeholder-expression/copy-default-collection-items
                                       (:eid cphe)])
       :add-item/ignore-input? true}))

   :list/item-labels-editable? false})

(defn collection-item-label [i]
  (str "Collection item #" @(store/get-value i :collection-item/order)))

(defn entity-view-cfg-collection-item [i]
  {:entity/ref i
   :entity/label [collection-item-label i]
   :entity/query-param [:collection-item/query-params i]
   :validation/schema :collection-item
   :validation/render-type :summary
   :validation/pull-pattern pull-pattern-collection-item
   ;; show the singular placeholder expression values within the collection item
   ;; in order of placeholder visualization order within the collection structure
   :list-item/details
   [lv/view
    {:entity/ref i
     :list/items :collection-item/override
     :list/sort-by visualization-order
     :list/->item-cfg entity-view-cfg
     :list/allow-delete-items? false}]})

(defmethod placeholder-expression :collection
  [{:keys [disabled?]} collection-placeholder cphe]
  [:> Stack {:divider ($ stack-divider)
             :mx -2
             :my -1}
   ;; product helper to generate collection items based on a product selector
   (when (and (not disabled?)
              (seq @(rf/subscribe [:ocp.product-helper/products-context-source-options])))
     [:> Box {:p 3}
      [:> Button
       {:on-click #(rf/dispatch [:ocp/open {:id :content-configuration-product-helper
                                            :props {:collection-placeholder collection-placeholder
                                                    :eid (:eid cphe)}}])
        :variant "text"
        :start-icon ($ Icon "shopping_basket")}
       "Product helper"]])

   ;; Shows the list of collection items within the collection placeholder
   ;; Each collection item has a link to its specific slide in the entity view.
   ;; The singular placeholders within the item are not shown
   [lv/view
    (assoc
     (list-collection-item-cfg cphe)
     :list/->item-cfg
     #(dissoc (entity-view-cfg-collection-item %) :list-item/details))]])

(defn list-cfg-defaults [cc]
  (lv/as-entity
   {:entity/ref cc
    :entity/label "Default values"
    :entity/icon "menu_book"
    :validation/schema :content-configuration
    :validation/pull-pattern [{:content-configuration/placeholder pull-pattern-placeholder}]
    :list/items [:placeholder-expression/default-ids (:eid cc)]
    :list/sort-by visualization-order
    :list/->item-cfg entity-view-cfg
    :list/allow-delete-items? false
    :list/empty-msg ($ ui.list/empty-msg {:title "Set your default values"}
                       ($ :p
                          "Your Adset requires a fallback version when no context is available,
                           or if the customer does not belong in any pre-defined categories.")
                       ($ :p
                          "To set default values, you should first "
                          ($ ui.list/empty-msg-link {:menu :placeholders} "add placeholders")
                          " to your Adset."))}))

(defmethod query-param->config :collection-item
  [{placeholder-name :placeholder rule-name :rule item-nr :item-nr :or {item-nr 1}}]
  (when-let [p @(rf/subscribe [:collection-placeholder/find-by-name placeholder-name])]
    (when-let [cphe (some-> @(rf/subscribe (if rule-name
                                             [:rule/find-assignment [:rule/name rule-name] p]
                                             [:ui.store/value p :placeholder/default-value]))
                            store/->DataScriptDBEntity)]
      (assoc (entity-view-cfg cphe)
             :entity/content
             [slide/view
              (-> (list-collection-item-cfg cphe)
                  (update :list/add-item-cfg first)
                  (merge {:slide/continue-cycle? true
                          :slide/content :list-item/details
                          :slide/current (some-> @(rf/subscribe [:collection-item/find-by-index (:eid cphe) (util/str->int item-nr)])
                                                 store/->DataScriptDBEntity)
                          :slide/slide-idx->query-param (fn [idx] {:item-nr (inc idx)})
                          :list/allow-delete-items? true
                          :list/->item-cfg entity-view-cfg-collection-item}))]))))
